import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Pagination from '../components/Pagination';
import { useSettings } from '../context/SettingsContext';

const PAGE_SIZES = [10, 25, 50];

export default function AuditLogs() {
  const { t } = useSettings();
  const [logs,       setLogs]       = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [page,       setPage]       = useState(1);
  const [pageSize,   setPageSize]   = useState(25);
  const [totalPages, setTotalPages] = useState(1);
  const [total,      setTotal]      = useState(0);

  // Reset to page 1 when pageSize changes
  useEffect(() => { setPage(1); }, [pageSize]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/api/audit-logs?page=${page}&limit=${pageSize}`);
      setLogs(res.data.logs);
      setTotalPages(res.data.pages);
      setTotal(res.data.total);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const months = t('monthsShort');

  const actionMeta = (action) => ({
    ADD_SUBSCRIPTION:    { label: t('paymentAdded'),   color: 'var(--green)',        bg: 'var(--green-bg)',            icon: '✓' },
    UPDATE_SUBSCRIPTION: { label: t('paymentUpdated'), color: 'var(--accent)',       bg: 'rgba(201,168,76,0.12)',      icon: '✎' },
    DELETE_SUBSCRIPTION: { label: t('paymentDeleted'), color: 'var(--red)',          bg: 'var(--red-bg)',              icon: '✗' },
    CREATE_ADMIN:        { label: t('adminCreated'),       color: 'var(--green)',   bg: 'var(--green-bg)',        icon: '+' },
    UPDATE_ADMIN:        { label: t('adminUpdated'),       color: 'var(--accent)',  bg: 'rgba(201,168,76,0.12)', icon: '✎' },
    DELETE_ADMIN:        { label: t('adminDeleted'),       color: 'var(--red)',     bg: 'var(--red-bg)',         icon: '✗' },
    REQUEST_APPROVED:    { label: t('REQUEST_APPROVED'),   color: 'var(--green)',   bg: 'var(--green-bg)',        icon: '✓' },
    REQUEST_REJECTED:    { label: t('REQUEST_REJECTED'),   color: 'var(--red)',     bg: 'var(--red-bg)',         icon: '✗' },
    ASSIGN_RESTAURANTS:  { label: t('ASSIGN_RESTAURANTS'), color: 'var(--accent)',  bg: 'rgba(201,168,76,0.12)', icon: '◈' },
    CREATE_PLAN:         { label: t('CREATE_PLAN'),         color: 'var(--green)',   bg: 'var(--green-bg)',        icon: '✦' },
    UPDATE_PLAN:         { label: t('UPDATE_PLAN'),         color: 'var(--accent)',  bg: 'rgba(201,168,76,0.12)', icon: '✎' },
    DELETE_PLAN:         { label: t('DELETE_PLAN'),         color: 'var(--red)',     bg: 'var(--red-bg)',         icon: '✗' },
    UPDATE_RESTAURANT:   { label: t('UPDATE_RESTAURANT'),   color: 'var(--accent)',  bg: 'rgba(201,168,76,0.12)', icon: '✎' },
  }[action] || { label: action, color: 'var(--text-muted)', bg: 'var(--gray-bg)', icon: '·' });

  const formatDetails = (log) => {
    const d = log.details || {};
    if (log.action === 'ADD_SUBSCRIPTION' || log.action === 'DELETE_SUBSCRIPTION') {
      const month = d.month ? months[d.month - 1] : '?';
      return `${d.restaurantName || '?'} — ${month} ${d.year || ''} · ${d.amount ? d.amount.toLocaleString() + ' UZS' : ''} ${d.paymentMethod ? '(' + t(d.paymentMethod) + ')' : ''}`;
    }
    return JSON.stringify(d);
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 style={{ fontSize: 34, marginBottom: 6 }}>{t('auditTitle')}</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>
            {t('auditSubtitle')} — {total} {t('totalEvents')}
          </p>
        </div>
      </div>

      <div className="card table-scroll" style={{ padding: 0 }}>
        {/* Page-size selector */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '12px 16px', borderBottom: '1px solid var(--border)',
          justifyContent: 'flex-end',
        }}>
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{t('rowsPerPage')}:</span>
          <select className="select-control" value={pageSize} onChange={e => setPageSize(Number(e.target.value))} style={{ width: 70 }}>
            {PAGE_SIZES.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>{t('loading')}</div>
        ) : logs.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>{t('noActivity')}</div>
        ) : (
          <>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 520 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  {[t('timestamp'), t('admin'), t('action'), t('details')].map(h => (
                    <th key={h} style={{ padding: '13px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {logs.map(log => {
                  const meta = actionMeta(log.action);
                  return (
                    <tr key={log._id}
                      style={{ borderBottom: '1px solid var(--border)', transition: 'background 0.12s' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--table-hover)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <td style={{ padding: '13px 16px', fontSize: 13, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                        <div>{new Date(log.timestamp).toLocaleDateString('en-GB')}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>
                          {new Date(log.timestamp).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </td>
                      <td style={{ padding: '13px 16px' }}>
                        <span className="badge badge-gold">{log.adminUsername}</span>
                      </td>
                      <td style={{ padding: '13px 16px', whiteSpace: 'nowrap' }}>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 5,
                          padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 500,
                          color: meta.color, background: meta.bg,
                        }}>
                          {meta.icon} {meta.label}
                        </span>
                      </td>
                      <td style={{ padding: '13px 16px', fontSize: 13, color: 'var(--text-muted)', minWidth: 200 }}>
                        {formatDetails(log)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <Pagination
              page={page}
              totalPages={totalPages}
              total={total}
              pageSize={pageSize}
              onPage={setPage}
            />
          </>
        )}
      </div>
    </div>
  );
}
